import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  addDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db, auth } from './config';
import type {
  StepProgress,
  Fellowship,
  ReflectionEntry,
  HomeworkAssignment,
  HomeworkTemplate,
  StepNumber,
  Step5Session,
  AmendsEntry,
  AmendMade,
} from '../../types/stepWork';

/**
 * Step Work Firestore Service
 *
 * CRUD operations for step progress, homework assignments, and templates.
 * Includes multi-fellowship support and sponsor review workflows.
 */

// ============================================
// Step Progress Operations
// ============================================

/**
 * Get all step progress for a user across all fellowships
 */
export async function getUserStepProgress(userId: string): Promise<StepProgress[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const progressRef = collection(db, 'users', userId, 'stepProgress');
  // Fetch without ordering to avoid index requirement, sort client-side
  const snapshot = await getDocs(progressRef);

  const progress = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as StepProgress[];

  // Sort client-side by fellowship and stepNumber
  return progress.sort((a, b) => {
    if (a.fellowship !== b.fellowship) {
      return a.fellowship.localeCompare(b.fellowship);
    }
    return a.stepNumber - b.stepNumber;
  });
}

/**
 * Get all pending review step progress items for a sponsor
 * Returns steps from all protégés that are pending this sponsor's review
 */
export async function getSponsorPendingReviews(_sponsorId: string): Promise<Array<StepProgress & { protegeId: string }>> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  // Note: This requires querying across user subcollections
  // For now, we'll return empty array and handle this with a collection group query
  // or by loading each protégé's data individually

  // TODO: Implement collection group query for better performance
  // For MVP, sponsors will navigate directly to review pages from notifications

  return [];
}

/**
 * Get step progress for a specific fellowship
 */
export async function getFellowshipStepProgress(
  userId: string,
  fellowship: Fellowship
): Promise<StepProgress[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const progressRef = collection(db, 'users', userId, 'stepProgress');
  const q = query(progressRef, where('fellowship', '==', fellowship));
  const snapshot = await getDocs(q);

  const progress = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as StepProgress[];

  // Sort client-side by stepNumber
  return progress.sort((a, b) => a.stepNumber - b.stepNumber);
}

/**
 * Get a specific step's progress
 */
export async function getStepProgress(
  userId: string,
  fellowship: Fellowship,
  stepNumber: StepNumber
): Promise<StepProgress | null> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const progressRef = collection(db, 'users', userId, 'stepProgress');
  const q = query(
    progressRef,
    where('fellowship', '==', fellowship),
    where('stepNumber', '==', stepNumber)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as StepProgress;
}

/**
 * Initialize a new fellowship's step progression
 * Creates Step 1 in 'not_started' status (only if it doesn't already exist)
 */
export async function initializeFellowshipSteps(
  userId: string,
  fellowship: Fellowship,
  sponsorId?: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  // Check if Step 1 already exists for this fellowship
  const existingStep1 = await getStepProgress(userId, fellowship, 1);
  if (existingStep1) {
    console.log(`Step 1 for ${fellowship} already exists, skipping initialization`);
    return;
  }

  const progressRef = collection(db, 'users', userId, 'stepProgress');

  // Create Step 1 as the entry point
  const step1: Partial<StepProgress> = {
    userId,
    fellowship,
    stepNumber: 1,
    status: 'not_started',
    startedAt: null,
    completedAt: null,
    submittedForReviewAt: null,
    sponsorId: sponsorId || null,
    sponsorConfirmedAt: null,
    sponsorComments: '',
    reflectionEntries: [],
    sharedWithSponsor: false,
    notes: '',
    createdAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
  };

  await addDoc(progressRef, step1);
  console.log(`Initialized Step 1 for ${fellowship}`);
}

/**
 * Start working on a step (changes status to 'in_progress')
 */
export async function startStep(
  userId: string,
  stepProgressId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);

  await updateDoc(stepRef, {
    status: 'in_progress',
    startedAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Save a reflection answer
 */
export async function saveReflectionAnswer(
  userId: string,
  stepProgressId: string,
  entry: ReflectionEntry
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);
  const stepDoc = await getDoc(stepRef);

  if (!stepDoc.exists()) throw new Error('Step progress not found');

  const stepData = stepDoc.data() as StepProgress;
  const existingEntries = stepData.reflectionEntries || [];

  // Update existing or add new
  const entryIndex = existingEntries.findIndex(e => e.id === entry.id);
  if (entryIndex >= 0) {
    existingEntries[entryIndex] = {
      ...entry,
      lastEditedAt: Timestamp.now(),
    };
  } else {
    existingEntries.push(entry);
  }

  await updateDoc(stepRef, {
    reflectionEntries: existingEntries,
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Save Step 3 commitment text
 */
export async function saveCommitment(
  userId: string,
  stepProgressId: string,
  commitmentText: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);

  await updateDoc(stepRef, {
    commitmentText,
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Save Step 5 session
 */
export async function saveStep5Session(
  userId: string,
  stepProgressId: string,
  session: Step5Session
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);
  const stepDoc = await getDoc(stepRef);

  if (!stepDoc.exists()) throw new Error('Step progress not found');

  const stepData = stepDoc.data() as StepProgress;
  const existingSessions = stepData.step5Sessions || [];

  // Update existing or add new
  const sessionIndex = existingSessions.findIndex(s => s.id === session.id);
  if (sessionIndex >= 0) {
    existingSessions[sessionIndex] = {
      ...session,
      lastUpdatedAt: Timestamp.now(),
    };
  } else {
    existingSessions.push(session);
  }

  await updateDoc(stepRef, {
    step5Sessions: existingSessions,
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Submit step for sponsor review
 */
export async function submitStepForReview(
  userId: string,
  stepProgressId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);

  await updateDoc(stepRef, {
    status: 'pending_review',
    submittedForReviewAt: Timestamp.now(),
    sharedWithSponsor: true,
    lastUpdatedAt: Timestamp.now(),
  });

  // TODO: Send notification to sponsor
}

/**
 * Sponsor confirms step completion
 */
export async function confirmStepCompletion(
  userId: string,
  stepProgressId: string,
  sponsorComments: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);
  const stepDoc = await getDoc(stepRef);

  if (!stepDoc.exists()) throw new Error('Step progress not found');

  const stepData = stepDoc.data() as StepProgress;

  await updateDoc(stepRef, {
    status: 'completed',
    completedAt: Timestamp.now(),
    sponsorConfirmedAt: Timestamp.now(),
    sponsorComments,
    lastUpdatedAt: Timestamp.now(),
  });

  // Create next step if not already exists
  const nextStepNumber = (stepData.stepNumber + 1) as StepNumber;
  if (nextStepNumber <= 12) {
    await createNextStep(userId, stepData.fellowship, nextStepNumber, stepData.sponsorId || undefined);
  }
}

/**
 * Self-confirm step (when no sponsor)
 */
export async function selfConfirmStep(
  userId: string,
  stepProgressId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);
  const stepDoc = await getDoc(stepRef);

  if (!stepDoc.exists()) throw new Error('Step progress not found');

  const stepData = stepDoc.data() as StepProgress;

  await updateDoc(stepRef, {
    status: 'completed',
    completedAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
  });

  // Create next step
  const nextStepNumber = (stepData.stepNumber + 1) as StepNumber;
  if (nextStepNumber <= 12) {
    await createNextStep(userId, stepData.fellowship, nextStepNumber, stepData.sponsorId || undefined);
  }
}

/**
 * Create the next step in sequence
 */
async function createNextStep(
  userId: string,
  fellowship: Fellowship,
  stepNumber: StepNumber,
  sponsorId?: string
): Promise<void> {
  const progressRef = collection(db, 'users', userId, 'stepProgress');

  const nextStep: Partial<StepProgress> = {
    userId,
    fellowship,
    stepNumber,
    status: 'not_started',
    startedAt: null,
    completedAt: null,
    submittedForReviewAt: null,
    sponsorId: sponsorId || null,
    sponsorConfirmedAt: null,
    sponsorComments: '',
    reflectionEntries: [],
    sharedWithSponsor: false,
    notes: '',
    createdAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
  };

  await addDoc(progressRef, nextStep);
}

/**
 * Update step notes
 */
export async function updateStepNotes(
  userId: string,
  stepProgressId: string,
  notes: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);

  await updateDoc(stepRef, {
    notes,
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Share step with sponsor
 */
export async function shareStepWithSponsor(
  userId: string,
  stepProgressId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const stepRef = doc(db, 'users', userId, 'stepProgress', stepProgressId);

  await updateDoc(stepRef, {
    sharedWithSponsor: true,
    lastUpdatedAt: Timestamp.now(),
  });
}

// ============================================
// Custom Fellowship Operations
// ============================================

export interface CustomFellowshipSuggestion {
  id?: string;
  name: string;
  abbreviation: string;
  description: string;
  website?: string;
  createdBy: string; // user ID
  createdAt: Timestamp;
  userCount: number; // How many users have used this
  lastUsedAt?: Timestamp;
}

/**
 * Save a custom fellowship suggestion for analytics
 * Increments user count if fellowship already exists
 */
export async function saveCustomFellowshipSuggestion(
  data: {
    name: string;
    abbreviation: string;
    description: string;
    website?: string;
  },
  userId: string
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const suggestionsRef = collection(db, 'fellowshipSuggestions');

  // Check if this fellowship already exists
  const existingQuery = query(
    suggestionsRef,
    where('abbreviation', '==', data.abbreviation.toUpperCase())
  );
  const existingSnapshot = await getDocs(existingQuery);

  if (!existingSnapshot.empty) {
    // Fellowship exists - increment user count
    const existingDoc = existingSnapshot.docs[0];
    await updateDoc(existingDoc.ref, {
      userCount: (existingDoc.data().userCount || 1) + 1,
      lastUsedAt: Timestamp.now(),
    });
    return existingDoc.id;
  }

  // Create new suggestion
  const newSuggestion: Omit<CustomFellowshipSuggestion, 'id'> = {
    name: data.name,
    abbreviation: data.abbreviation.toUpperCase(),
    description: data.description,
    website: data.website,
    createdBy: userId,
    createdAt: Timestamp.now(),
    userCount: 1,
    lastUsedAt: Timestamp.now(),
  };

  const docRef = await addDoc(suggestionsRef, newSuggestion);
  return docRef.id;
}

/**
 * Save a custom fellowship to user's profile
 * This allows the user to track steps for custom fellowships
 */
export async function saveUserCustomFellowship(
  userId: string,
  data: {
    name: string;
    abbreviation: string;
    description: string;
    website?: string;
  }
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  // Save to user's customFellowships subcollection
  const customFellowshipsRef = collection(db, 'users', userId, 'customFellowships');

  const customFellowship = {
    ...data,
    abbreviation: data.abbreviation.toUpperCase(),
    createdAt: Timestamp.now(),
  };

  await addDoc(customFellowshipsRef, customFellowship);

  // Also save to analytics collection
  await saveCustomFellowshipSuggestion(data, userId);
}

/**
 * Get user's custom fellowships
 */
export async function getUserCustomFellowships(userId: string): Promise<any[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const customFellowshipsRef = collection(db, 'users', userId, 'customFellowships');
  const snapshot = await getDocs(customFellowshipsRef);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}

// ============================================
// Homework Assignment Operations
// ============================================

/**
 * Get all homework for a specific step
 */
export async function getStepHomework(
  userId: string,
  stepProgressId: string
): Promise<HomeworkAssignment[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const homeworkRef = collection(
    db,
    'users',
    userId,
    'stepProgress',
    stepProgressId,
    'homework'
  );
  const q = query(homeworkRef, orderBy('assignedAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as HomeworkAssignment[];
}

/**
 * Create homework assignment
 */
export async function createHomeworkAssignment(
  protegeId: string,
  stepProgressId: string,
  assignment: Omit<HomeworkAssignment, 'id' | 'createdAt' | 'lastUpdatedAt'>
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const homeworkRef = collection(
    db,
    'users',
    protegeId,
    'stepProgress',
    stepProgressId,
    'homework'
  );

  const docRef = await addDoc(homeworkRef, {
    ...assignment,
    createdAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
  });

  return docRef.id;
}

/**
 * Update homework assignment
 */
export async function updateHomeworkAssignment(
  userId: string,
  stepProgressId: string,
  homeworkId: string,
  updates: Partial<HomeworkAssignment>
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const homeworkRef = doc(
    db,
    'users',
    userId,
    'stepProgress',
    stepProgressId,
    'homework',
    homeworkId
  );

  await updateDoc(homeworkRef, {
    ...updates,
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Mark homework as started
 */
export async function startHomework(
  userId: string,
  stepProgressId: string,
  homeworkId: string
): Promise<void> {
  await updateHomeworkAssignment(userId, stepProgressId, homeworkId, {
    status: 'in_progress',
    startedAt: Timestamp.now(),
  });
}

/**
 * Mark homework as completed
 */
export async function completeHomework(
  userId: string,
  stepProgressId: string,
  homeworkId: string,
  completionNotes?: string
): Promise<void> {
  await updateHomeworkAssignment(userId, stepProgressId, homeworkId, {
    status: 'completed',
    completedAt: Timestamp.now(),
    completionNotes,
  });
}

/**
 * Delete homework assignment
 */
export async function deleteHomeworkAssignment(
  userId: string,
  stepProgressId: string,
  homeworkId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const homeworkRef = doc(
    db,
    'users',
    userId,
    'stepProgress',
    stepProgressId,
    'homework',
    homeworkId
  );

  await deleteDoc(homeworkRef);
}

// ============================================
// Homework Template Operations
// ============================================

/**
 * Get sponsor's homework templates
 */
export async function getHomeworkTemplates(
  sponsorId: string
): Promise<HomeworkTemplate[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const templatesRef = collection(db, 'users', sponsorId, 'homeworkTemplates');
  const q = query(templatesRef, orderBy('lastUsedAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as HomeworkTemplate[];
}

/**
 * Create homework template
 */
export async function createHomeworkTemplate(
  sponsorId: string,
  template: Omit<HomeworkTemplate, 'id' | 'createdAt' | 'lastUpdatedAt'>
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const templatesRef = collection(db, 'users', sponsorId, 'homeworkTemplates');

  const docRef = await addDoc(templatesRef, {
    ...template,
    createdAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
  });

  return docRef.id;
}

/**
 * Update homework template
 */
export async function updateHomeworkTemplate(
  sponsorId: string,
  templateId: string,
  updates: Partial<HomeworkTemplate>
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const templateRef = doc(db, 'users', sponsorId, 'homeworkTemplates', templateId);

  await updateDoc(templateRef, {
    ...updates,
    lastUpdatedAt: Timestamp.now(),
  });
}

/**
 * Delete homework template
 */
export async function deleteHomeworkTemplate(
  sponsorId: string,
  templateId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const templateRef = doc(db, 'users', sponsorId, 'homeworkTemplates', templateId);
  await deleteDoc(templateRef);
}

/**
 * Apply template to protégé's step
 * Creates homework assignments from template
 */
export async function applyHomeworkTemplate(
  sponsorId: string,
  protegeId: string,
  stepProgressId: string,
  templateId: string,
  stepNumber: StepNumber,
  fellowship: Fellowship
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  // Get template
  const templateRef = doc(db, 'users', sponsorId, 'homeworkTemplates', templateId);
  const templateDoc = await getDoc(templateRef);

  if (!templateDoc.exists()) {
    throw new Error('Template not found');
  }

  const template = templateDoc.data() as HomeworkTemplate;

  // Get assignments for this step
  const stepAssignments = template.stepAssignments[stepNumber.toString()] || [];

  if (stepAssignments.length === 0) {
    return; // No assignments for this step
  }

  // Create homework assignments
  const batch = writeBatch(db);

  stepAssignments.forEach(item => {
    const homeworkRef = doc(
      collection(db, 'users', protegeId, 'stepProgress', stepProgressId, 'homework')
    );

    batch.set(homeworkRef, {
      stepProgressId,
      stepNumber,
      fellowship,
      assignedBy: sponsorId,
      assignedTo: protegeId,
      assignedAt: Timestamp.now(),
      title: item.title,
      description: item.description,
      type: item.type,
      readingReference: item.readingReference,
      dueDate: item.estimatedDays
        ? Timestamp.fromDate(
            new Date(Date.now() + item.estimatedDays * 24 * 60 * 60 * 1000)
          )
        : undefined,
      status: 'assigned',
      fromTemplate: templateId,
      createdAt: Timestamp.now(),
      lastUpdatedAt: Timestamp.now(),
    });
  });

  await batch.commit();

  // Update template usage stats
  await updateDoc(templateRef, {
    appliedToCount: (template.appliedToCount || 0) + 1,
    lastUsedAt: Timestamp.now(),
  });
}

// ============================================
// Step 8: Amends List Operations
// ============================================

/**
 * Get all amends entries for a user and fellowship
 */
export async function getAmendsEntries(
  userId: string,
  fellowship: Fellowship
): Promise<AmendsEntry[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const amendsRef = collection(db, 'users', userId, 'amendsEntries');
  const q = query(amendsRef, where('fellowship', '==', fellowship));
  const snapshot = await getDocs(q);

  const entries = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as AmendsEntry[];

  // Sort by createdAt ascending
  return entries.sort((a, b) => {
    const aTime = a.createdAt?.toMillis() ?? 0;
    const bTime = b.createdAt?.toMillis() ?? 0;
    return aTime - bTime;
  });
}

/**
 * Add a new amends entry (Step 8 list row)
 */
export async function addAmendsEntry(
  userId: string,
  fellowship: Fellowship,
  entry: Omit<AmendsEntry, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const amendsRef = collection(db, 'users', userId, 'amendsEntries');
  const docRef = await addDoc(amendsRef, {
    ...entry,
    fellowship,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  return docRef.id;
}

/**
 * Update an amends entry
 */
export async function updateAmendsEntry(
  userId: string,
  entryId: string,
  updates: Partial<Omit<AmendsEntry, 'id' | 'createdAt'>>
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const entryRef = doc(db, 'users', userId, 'amendsEntries', entryId);
  await updateDoc(entryRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete an amends entry
 */
export async function deleteAmendsEntry(
  userId: string,
  entryId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const entryRef = doc(db, 'users', userId, 'amendsEntries', entryId);
  await deleteDoc(entryRef);
}

// ============================================
// Step 9: Amends Made Operations
// ============================================

/**
 * Get all amends made for a user and fellowship
 */
export async function getAmendsMade(
  userId: string,
  fellowship: Fellowship
): Promise<AmendMade[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const amendsRef = collection(db, 'users', userId, 'amendsMade');
  const q = query(amendsRef, where('fellowship', '==', fellowship));
  const snapshot = await getDocs(q);

  const amends = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as AmendMade[];

  // Sort by dateOfAmend ascending
  return amends.sort((a, b) => {
    const aTime = a.dateOfAmend?.toMillis() ?? 0;
    const bTime = b.dateOfAmend?.toMillis() ?? 0;
    return aTime - bTime;
  });
}

/**
 * Add a new amend made (Step 9 tracking entry)
 */
export async function addAmendMade(
  userId: string,
  fellowship: Fellowship,
  amend: Omit<AmendMade, 'id' | 'createdAt'>
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const amendsRef = collection(db, 'users', userId, 'amendsMade');
  const docRef = await addDoc(amendsRef, {
    ...amend,
    fellowship,
    createdAt: Timestamp.now(),
  });

  return docRef.id;
}

/**
 * Delete an amend made entry
 */
export async function deleteAmendMade(
  userId: string,
  amendId: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const amendRef = doc(db, 'users', userId, 'amendsMade', amendId);
  await deleteDoc(amendRef);
}
